import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Icon,
  Label,
  Loader,
  Message,
  Popup,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { LabelChooser } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";

import actions from "actions";
import api from "api";
import {
  type ChallengesReducer,
  defaultInitialState as challengeReducerDefaultState,
} from "reducers/challenges";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { ChallengeChooser, UserChooser } from "components/lib/Choosers";
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning";
import ActionDropdown, {
  type Action,
} from "components/lib/ConfigurableTable/ActionDropdown";
import ConfirmAnnouncementModal from "components/lib/ConfirmAnnouncementModal";
import ChallengeIdeaFilters from "components/lib/Filters/ChallengeIdeaFilters";
import { ChallengeFilterKeys } from "components/lib/Filters/filterUtils";
import { GatedChallengeModal } from "components/lib/GatedChallengeModal";
import { GroupUserManagementModal } from "components/lib/GroupUserManagementModal";
import { confirmMerge } from "components/lib/IdeaMerge";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";
import IdeaCardView from "components/lib/Ideas/IdeaView/IdeaCardView";
// import IdeaListView from "components/lib/Ideas/IdeaView/IdeaListView/IdeaListView";
import IdeaTableView from "components/lib/Ideas/IdeaView/IdeaTableView";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";
import { useTrayStatus } from "components/lib/Notifications";
import ProductTour from "components/lib/ProductTour";
import ProjectBoard from "components/lib/ProjectBoard";
import BoardEvents from "components/lib/ProjectBoard/ProjectEvents/BoardEvents";
import { EmptyBox } from "components/lib/UI";

import {
  IdeaDeadlineModal,
  IdeaDeletionModal,
  IdeaManagementDeadlineModal,
  IdeaMoveToLaneModal,
  Pagination,
} from "./IdeaActions";
import ChallengeMyIdeas from "./MyIdeas";
import { mergeBasicIdeasRefetch, useIdeaEnrichment } from "./useIdeaEnrichment";
import { useIdeaQueryParams } from "./useIdeaQueryParams";

const StyledIdeasContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const StyledIdeaItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 100%;
  flex: 1;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) =>
    theme.sizes.isMobile ? "column-reverse" : "row"};
  align-items: ${({ theme }) =>
    !theme.sizes.isMobile ? "flex-start" : "center"};
  justify-content: space-between;
  margin-bottom: 5px;
`;

const StyledFilters = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: center;
  gap: 10px;
  .ui.button,
  a,
  .ui.dropdown {
    min-width: fit-content;
    ${({ theme }) =>
      theme.sizes.isMobile &&
      `
      margin-bottom: 5px;
      width: 100%;
    `}
  }
  .ui.button,
  .ui.dropdown {
    height: 33px;
  }
  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
    width: 100%;
  `}
  ${({ theme }) =>
    !theme.sizes.isLargeComputer &&
    `
    margin-bottom: 5px;
  `}
`;

const IdeaViewTypes = [
  { key: "card", text: "Card", value: "card", icon: "vcard" },
  { key: "list", text: "List", value: "list", icon: "list" },
  { key: "board", text: "Board", value: "board", icon: "trello" },
];

type ChallengeIdea =
  OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][number];

type IdeasProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  newIdea: () => void;
};

const Ideas = ({ challenge, newIdea }: IdeasProps) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const previewId = searchParams.get("idea");

  const [showMyIdeas, setShowMyIdeas] = useState(true);

  const [challengeChooserIdea, setChallengeChooserIdea] = useState<string[]>(
    [],
  );
  const [_selectedIdeas, setSelectedIdeas] = useState<ChallengeIdea[]>([]);
  const [assessmentAssignIdeas, setAssessmentAssignIdeas] = useState<string[]>(
    [],
  );
  const [assessmentAssignRoundId, setAssessmentAssignRoundId] = useState("");
  const [nextPhaseChallenge, setNextPhaseChallenge] = useState<string[]>([]);
  const [deletingIdeas, setDeletingIdeas] = useState<ChallengeIdea[]>([]);
  const [labelIdeas, setLabelIdeas] = useState<ChallengeIdea[]>([]);
  const [projectAssignIdeas, setProjectAssignIdeas] = useState<string[]>([]);
  const [deadlineIdeas, setDeadlineIdeas] = useState<ChallengeIdea[]>([]);
  const [managementDeadlineIdeas, setManagementDeadlineIdeas] = useState<
    ChallengeIdea[]
  >([]);
  const [moveLaneIdeas, setMoveLaneIdeas] = useState<ChallengeIdea[]>([]);
  const [groupAddIdeas, setGroupAddIdeas] = useState<ChallengeIdea[]>([]);
  const [removingIdeaFromMerge, setRemovingIdeaFromMerge] = useState("");
  const [confirmAnnouncementModalOpen, setConfirmAnnouncementModalOpen] =
    useState(false);
  const [laneAnnouncements, setLaneAnnouncements] = useState<
    Array<OpenAPI.Schemas["Announcement"]>
  >([]);
  const [laneUpdateContext, setLaneUpdateContext] = useState<{
    ideaIds?: string[];
    laneId?: string;
    order?: number | null;
  }>({});

  const user: OpenAPI.GET<"/users/me">["response"] | undefined = useAppSelector(
    (state) => state.user,
  );
  const hasUser: boolean = !!user;
  const { ownerOrganisation } = user ?? {};
  const ideaMergeEnabled =
    ownerOrganisation?.enabledFeatures?.includes("ideaMerging");
  const canMakeIdea = util.canCreateIdea(user, challenge);

  const challengeId = challenge._id!;
  const shouldFetchIdeas = useMemo(
    () => !!challengeId && hasUser,
    [challengeId, hasUser],
  );

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const canActionIdeas = useMemo(
    () => util.canActionIdeas(user, challenge),
    [user, challenge],
  );
  const canViewProjectBoard = useMemo(
    () =>
      util.hasPermission(user, "challenge.viewProjectBoard", challenge._id) ||
      challenge.projectManagementVisibility === "users",
    [user, challenge],
  );

  const challengesReducer: ChallengesReducer = useAppSelector(
    (state) => state.challenges,
  );
  const {
    ideaIncludes: filter,
    ideaPage: page,
    ideaLimit: limit,
    projectBoardLanesFilter,
    ideaViewType,
    filtersVisible,
    postFetchPreviewIndex,
  } = challengesReducer;

  const queryParams = useIdeaQueryParams(ideaViewType);

  const basicIdeaQuery = useQuery(
    initUseQuery(
      api.challenges.getIdeas,
      [challengeId!, { ...queryParams, contentType: "basic" }],
      {
        isEnabled: shouldFetchIdeas,
        structuralSharing: mergeBasicIdeasRefetch,
      },
    ),
  );

  const { isEnriching } = useIdeaEnrichment(challengeId, queryParams, {
    enabled: shouldFetchIdeas && !!basicIdeaQuery.data,
    indexUpdatedAt: basicIdeaQuery.dataUpdatedAt,
  });

  const ideaFilterQuery = useQuery(
    initUseQuery(api.challenges.getIdeaFilters, [challengeId!], {
      isEnabled: shouldFetchIdeas,
    }),
  );

  const projectLaneQuery = useQuery(
    initUseQuery(api.boards.getProjectLanes, [challengeId!], {
      isEnabled: canManageChallenge || canViewProjectBoard,
    }),
  );

  const emptyIdeasResponse = useMemo(
    () =>
      ({
        ideas: [],
        nextPageAvailable: false,
        previousPageAvailable: false,
        total: 0,
        page: 1,
        ideasVotedFor: 0,
        invitations: [],
        laneImpactMeasures: {},
      }) as OpenAPI.GET<"/challenges/{id}/ideas">["response"],
    [],
  );

  const data = useMemo(() => {
    return basicIdeaQuery.data || emptyIdeasResponse;
  }, [basicIdeaQuery.data, emptyIdeasResponse]);

  const ideasLoading = basicIdeaQuery.isLoading;

  const setPreviewId = useCallback(
    (ideaId: string | null, options?: { toComments?: boolean }) => {
      setSearchParams((prev) => {
        if (ideaId) {
          prev.delete("focus");
          prev.set("idea", ideaId);
        } else {
          const highlight = prev.get("idea");
          if (highlight) {
            prev.set("focus", highlight);
          }
          prev.delete("idea");
        }
        return prev;
      });
      if (options?.toComments) {
        navigate(location.search + "#comments");
      }
    },
    [setSearchParams, location, navigate],
  );

  useEffect(() => {
    if (postFetchPreviewIndex === null || postFetchPreviewIndex === undefined) {
      return;
    }
    dispatch(
      actions.challenges.updateIdeaFilters({ postFetchPreviewIndex: null }),
    );
    const ideas = data.ideas || [];
    const ideaAtIndex = ideas[postFetchPreviewIndex] as ChallengeIdea & {
      _enriched?: boolean;
    };
    if (!ideaAtIndex) return;
    if (!ideaAtIndex._enriched && isEnriching) return;
    setPreviewId(ideaAtIndex._id!);
  }, [data.ideas, dispatch, isEnriching, postFetchPreviewIndex, setPreviewId]);

  const ideaMap = useMemo(() => {
    const map: { [key: string]: ChallengeIdea } = {};
    data.ideas.forEach((idea) => {
      map[idea._id!] = idea;
    });
    return map;
  }, [data.ideas]);

  const selectedIdeas = useMemo(() => {
    return _selectedIdeas.map((i) => {
      const matchedIdea = ideaMap[i._id!];
      return matchedIdea || i;
    });
  }, [ideaMap, _selectedIdeas]);

  const [selectingMultipleCards, setSelectingMultipleCards] = useState(false);

  const theme = useTheme();

  const paramViewType = searchParams.get("queryView");

  // For project board view, we only want to paginate within the lane of the currently previewed idea
  const paginatedIdeas = useMemo(() => {
    if (ideaViewType !== "board") return data.ideas;
    if (!previewId) return data.ideas;
    const previewIdea = ideaMap[previewId];
    const currentLaneId = previewIdea?.projectManagement?.boards?.find(
      (b) => b.forId === challengeId,
    )?.lane;
    const ideasInLane = data.ideas.filter(
      (i) =>
        i.projectManagement?.boards?.find((b) => b.forId === challengeId)
          ?.lane === currentLaneId,
    );
    // Now sort by order in lane
    const sortedIdeasInLane = ideasInLane.sort((a, b) => {
      const aOrder = a.projectManagement?.boards?.find(
        (b) => b.forId === challengeId,
      )?.order;
      const bOrder = b.projectManagement?.boards?.find(
        (b) => b.forId === challengeId,
      )?.order;
      return (aOrder || 0) - (bOrder || 0);
    });
    return sortedIdeasInLane;
  }, [ideaViewType, data.ideas, ideaMap, previewId, challengeId]);

  const canGoPrevIdea = useMemo(
    () =>
      paginatedIdeas.findIndex((i) => i._id === previewId) > 0 ||
      data.previousPageAvailable,
    [paginatedIdeas, data.previousPageAvailable, previewId],
  );
  const canGoNextIdea = useMemo(
    () =>
      paginatedIdeas.findIndex((i) => i._id === previewId) !==
        paginatedIdeas.length - 1 || data.nextPageAvailable,
    [paginatedIdeas, data.nextPageAvailable, previewId],
  );

  const currentPreviewIdInList = useMemo(
    () => data.ideas.some((i) => i._id === previewId),
    [data.ideas, previewId],
  );
  const template = challenge.ownerIdeaTemplate;
  const assessmentRounds = useMemo(
    () => template?.assessments?.rounds || [],
    [template],
  );

  const { ideaFilter } = useAppSelector((state) => state.challenges);

  const updateFilters = useCallback(
    (filters: Partial<ChallengesReducer>) => {
      dispatch(actions.challenges.updateIdeaFilters(filters));
    },
    [dispatch],
  );
  const updateSearchTerm = useCallback(
    (term) => {
      updateFilters({ ideaFilter: term, ideaPage: 1 });
    },
    [updateFilters],
  );

  const setIdeaViewType = useCallback(
    (viewType: string, resetParams = true) => {
      if (resetParams) {
        setSearchParams((prev) => {
          prev.delete("queryView");
          return prev;
        });
      }
      if (
        ["card", "list"].includes(viewType) &&
        util.localStorageIsSupported()
      ) {
        localStorage.setItem("ideaViewTypePreference", viewType);
      }

      dispatch(actions.challenges.setIdeaViewType(viewType));
    },
    [dispatch, setSearchParams],
  );

  useEffect(() => {
    if (
      paramViewType &&
      IdeaViewTypes.map((v) => v.value).includes(paramViewType)
    ) {
      setIdeaViewType(paramViewType, false);
    }
  }, [paramViewType, setIdeaViewType]);

  useEffect(() => {
    if (ideaViewType !== "board") {
      setSelectingMultipleCards(false);
    }
  }, [ideaViewType]);

  const movePreviewIdea = useCallback(
    (amount) => {
      const ideaIndex = paginatedIdeas.findIndex((i) => i._id === previewId);
      if (ideaIndex === -1) return;
      const newIndex = ideaIndex + amount;
      if (newIndex === -1 && data.previousPageAvailable) {
        // Set page to (current page - 1)
        // Set postFetchPreviewIndex to (limit - 1)
        updateFilters({ ideaPage: page - 1, postFetchPreviewIndex: limit - 1 });
      } else if (newIndex === data.ideas.length && data.nextPageAvailable) {
        // Set page to (current page + 1)
        // Set postFetchPreviewIndex to (0)
        updateFilters({ ideaPage: page + 1, postFetchPreviewIndex: 0 });
      } else {
        const newPreviewId = paginatedIdeas.map((i) => i._id!)[newIndex];
        setPreviewId(newPreviewId);
      }
    },
    [data, previewId, setPreviewId, page, limit, updateFilters, paginatedIdeas],
  );

  const onSelectIdea = useCallback((idea) => {
    setSelectedIdeas((prev) => {
      const prevIndex = prev.findIndex((i) => i._id === idea._id);
      if (prevIndex === -1) {
        return [...prev, idea];
      }
      prev.splice(prevIndex, 1);
      return [...prev];
    });
  }, []);

  const bulkUpdateStatus = useCallback(
    (selectedIdeas, statusKey, status) => {
      api.challenges.updateIdeasStatus(
        challengeId,
        { ideas: selectedIdeas, [statusKey]: status },
        ({ ideas: updatedIdeas }) => {
          toast(`${selectedIdeas.length} ${t("generic.ideas")} updated`);
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((u) => u._id === idea._id);
              if (!updatedIdea) return idea;
              return { ...idea, ...updatedIdea };
            }),
          }));
          // Requires full refetch to update pinned ideas which are now bubbled to top
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
        },
        () => {},
      );
    },
    [challengeId, t],
  );

  const bulkMoveIdeas = useCallback(
    (ideaIds, newChallengeId, onComplete = () => {}) => {
      api.ideas.moveToChallenge(
        ideaIds,
        newChallengeId,
        (data) => {
          toast.success(
            `${t("common:capitalise", { key: "generic.ideas" })} moved to ${t("generic.challenge")}`,
          );
          setSelectedIdeas([]);

          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
          api.invalidateQueries(api.challenges.getIdeas, [newChallengeId]);
          for (const id of ideaIds) {
            // Because we retain placeholder data ensure that the challenge and ownerChallenge are updated immediately, but still invalidate the query to get the latest data once opened
            api.setQueryData(api.ideas.get, [id], (prev) => ({
              ...prev,
              challenge: data.challenge,
              ownerChallenge: data.ownerChallenge,
              projectManagement: { boards: [{ forId: data.challenge }] },
            }));
            api.invalidateQueries(api.ideas.get, [id]);
          }
          if (onComplete) onComplete();
        },
        (err) => toast.error(err.message),
      );
    },
    [t, challengeId],
  );

  const createChallengeFromIdea = useCallback(
    (ideaId) => {
      api.challenges.createFromIdea(
        { idea: ideaId },
        (data) => {
          navigate(`/challenges/${data.challenge._id}`);
          toast.success("Challenge created");
        },
        (err) => toast.error(err.message),
      );
    },
    [navigate],
  );

  const removeIdeaFromMerge = useCallback(
    (
      mergeIdeaId: string,
      ideaId: string,
      onComplete = (_mergeIdeaId: string, _ideaId: string) => {},
      onError = (_mergeIdeaId: string, _ideaId: string) => {},
    ) => {
      // If you're removing the 2nd to last idea from a merge, then we delete the merge container, as there'd be no point in having it
      const mergeIdea = data.ideas.find((i) => i._id === mergeIdeaId);
      const willDeleteMergeContainer =
        mergeIdea?.children && mergeIdea.children.length <= 2;
      util
        .confirm(
          `Are you sure you want to remove this ${t("generic.idea")} from the merge?`,
          `The ${t("generic.idea")} will not be deleted and will remain in the ${t("generic.challenge")}. You can re-add it to the merge at any time. ${willDeleteMergeContainer ? `The merge container will be deleted as it will only contain one ${t("generic.idea")}.` : ""}`,
        )
        .then(() => {
          setRemovingIdeaFromMerge(ideaId);
          api.ideas.removeIdeaFromMerge(
            mergeIdeaId,
            ideaId,
            () => {
              if (willDeleteMergeContainer) {
                // If we delete the merge container, we get the ID of the idea that wasn't deleted, and change the preview ID to that
                const nonDeletedIdeaId = mergeIdea?.children?.find(
                  (c) => c !== ideaId,
                );
                if (nonDeletedIdeaId) {
                  setPreviewId(nonDeletedIdeaId);
                }
              }
              setRemovingIdeaFromMerge("");
              api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
              api.invalidateQueries(api.ideas.get, [ideaId]);
              api.invalidateQueries(api.ideas.get, [mergeIdeaId]);
              onComplete(mergeIdeaId, ideaId);
            },
            (err) => {
              toast.error(err.message);
              setRemovingIdeaFromMerge("");
              onError(mergeIdeaId, ideaId);
            },
          );
        })
        .catch(() => {});
    },
    [challengeId, data.ideas, t],
  );

  const updateProjectLane = useCallback(
    (
      ideaIds: string[],
      targetLane: string,
      order: number | null = null,
      announcementIds: Array<string>,
    ) => {
      api.boards.moveProjectIdeas(
        challengeId,
        { targetLane, ideaIds, order, sendAnnouncements: announcementIds },
        ({ updatedIdeas }) => {
          if (ideaIds.length === 1) {
            toast.success(`Moved ${t("generic.idea")}`);
          } else {
            toast.success(`Moved ${ideaIds.length} ${t("generic.ideas")}`);
          }
          setSelectedIdeas([]);
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas?.find((i) => i._id === idea._id);
              if (updatedIdea) {
                return {
                  ...idea,
                  projectManagement: updatedIdea.projectManagement,
                };
              }
              return idea;
            }),
          }));
          // Refetch required for project lane impacts
          api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
        },
        (err) => {
          toast.error(`Unable to move ${t("generic.ideas")}. ${err.message}`);
        },
      );
    },
    [challengeId, t],
  );

  const checkUpdateProjectLane = (
    ideaIds: string[],
    laneId: string,
    order: number | null = null,
  ) => {
    const newLane = projectLaneQuery.data?.lanes.find((l) => l._id === laneId);
    if (!newLane?.announcements) {
      updateProjectLane(ideaIds, laneId, order, []);
    } else {
      setLaneAnnouncements(newLane.announcements);
      setConfirmAnnouncementModalOpen(true);
      setLaneUpdateContext({ laneId, ideaIds, order });
    }
  };

  const updateIdeaProjectDeadlineMulti = useCallback(
    (deadline: Date) => {
      const formattedDeadline = new Date(deadline);
      api.boards.updateProjectIdeasDeadline(
        challengeId,
        {
          deadline: formattedDeadline,
          ideaIds: selectedIdeas.map((i) => i._id),
        },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
              if (!updatedIdea) return idea;

              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              };
            }),
          }));
          toast.success(
            `Updated deadline for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
        },
        (err) => {
          toast.error(`Unable to update deadline. ${err.message}`);
        },
      );
    },
    [challengeId, selectedIdeas, t],
  );

  const bulkProjectAssign = useCallback(
    (userIds: string[]) => {
      api.boards.bulkAssignProjectIdeas(
        challengeId,
        { ideaIds: selectedIdeas.map((i) => i._id), userIds },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => ({
            ...prev,
            ideas: prev.ideas.map((idea) => {
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id);
              if (!updatedIdea) return idea;
              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              };
            }),
          }));
          setSelectedIdeas([]);
          toast.success(
            `Assigned ${util.pluralise(userIds.length, "user", "users", true)} for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          );
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [challengeId, selectedIdeas, t],
  );

  const [, setTrayStatus] = useTrayStatus();

  const createIdeaPdfExport = useCallback(
    (ideas: ChallengeIdea[], appendCreditsafeReport: boolean) => {
      if (!user) return;
      if (!challenge) return;
      if (ideas.length === 0) return;

      const ideaIds = ideas.map((idea) => idea._id);
      const challengeName = challenge?.name || "Challenge";
      const displayName = `${challengeName} idea export.pdf`;

      api.users.createDownload(
        user._id,
        {
          downloadType: "ideaPdfExport",
          displayName,
          ideas: ideaIds,
          challenge: challenge._id,
          appendCreditsafeReport,
        },
        (download) => {
          api.setQueryData(
            api.users.getDownloads,
            [user._id],
            (prev) => ({
              ...prev,
              pendingDownloads: (prev?.pendingDownloads || []).concat([
                download,
              ]),
            }),
            { allowUndefined: true },
          );
          setTrayStatus({ open: true, activeIndex: 1 });
        },
        () => {},
      );
    },
    [challenge, setTrayStatus, user],
  );

  const ideaHasCreditsafeReport = useCallback(
    (ideas: ChallengeIdea[]) => {
      if (!user) return false;
      if (!challenge) return false;
      if (
        !(
          util.canManageChallenge(user, challenge) ||
          util.hasPermission(user, "challenge.viewIdeas", challenge?._id)
        )
      ) {
        return false;
      }

      const ideaTemplate: OpenAPI.Schemas["IdeaTemplate"] =
        (challenge as any).ownerIdeaTemplate ||
        (ideas.find((idea) => (idea as any).ownerIdeaTemplate) as any)
          ?.ownerIdeaTemplate ||
        (
          ideas.find(
            (idea) => (idea as any).ownerChallenge?.ownerIdeaTemplate,
          ) as any
        )?.ownerChallenge?.ownerIdeaTemplate;

      return !!ideaTemplate?.body?.find((section) =>
        section?.fields?.find((field) => field.type === "creditsafereport"),
      );
    },
    [challenge, user],
  );

  const ideaActions = useMemo<Action<ChallengeIdea>[]>(() => {
    if (!challenge) return [];

    const canShowBoardProjectActions =
      ideaViewType === "board" &&
      (canManageChallenge ||
        util.hasPermission(user, "challenge.moveProjectIdeas", challengeId) ||
        util.hasPermission(
          user,
          "challenge.editProjectAssignees",
          challengeId,
        ) ||
        util.hasPermission(user, "challenge.editProjectDates", challengeId));

    return [
      // Board-only project management bulk actions
      (items) => ({
        name: `Move all ${t("generic.ideas")} to project lane`,
        icon: "move",
        appear: "multi",
        isVisible:
          canShowBoardProjectActions &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.moveProjectIdeas",
              challengeId,
            )),
        onClick: () => setMoveLaneIdeas(items),
      }),
      (items) => ({
        name: `Project assign users to all ${t("generic.ideas")}`,
        appear: "multi",
        isVisible:
          canShowBoardProjectActions &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.editProjectAssignees",
              challengeId,
            )),
        onClick: () => setProjectAssignIdeas(items.map((i) => i._id)),
      }),
      (items) => ({
        name: "Set management deadline",
        icon: "calendar outline",
        appear: "both",
        isVisible:
          canShowBoardProjectActions &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.editProjectDates",
              challengeId,
            )),
        onClick: () => setManagementDeadlineIdeas(items),
      }),

      // Pin
      (items) => ({
        name:
          items.length === 1 ?
            items[0].isPinned ?
              "Unpin"
            : "Pin"
          : "Pin",
        icon: "pin",
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.pinIdeas", challengeId),
        tooltip: <PinIdeaHelper challenge={challenge} />,
        onClick: () =>
          bulkUpdateStatus(
            items.map((i) => i._id),
            "isPinned",
            !items.every((i) => i.isPinned),
          ),
      }),

      // Deadlines
      {
        name: "Set submission deadline",
        icon: "calendar",
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.editIdeaDeadlines", challengeId),
        onClick: (items) => setDeadlineIdeas(items),
      },

      // Groups
      {
        name: "Add authors to group",
        icon: "users",
        appear: "both",
        checkPermission: "org.addToGroupsDirectly",
        checkPermissionScope: user?.ownerOrganisation?._id,
        onClick: (items) => setGroupAddIdeas(items),
      },

      // Next phase challenge
      {
        name: `Move to next-phase`,
        appear: "multi",
        icon: "level up",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.editSettings", challengeId),
        onClick: (items) => setNextPhaseChallenge(items.map((i) => i._id)),
      },

      // Merge
      (items) => {
        const canMerge =
          items.length > 1 &&
          (items.filter((i) => i.parent || i.children).length === 0 ||
            (items.filter((i) => i.children).length === 1 &&
              items.filter((i) => !(i.parent || i.children)).length ===
                items.length - 1));

        return {
          name: "Merge",
          icon: "sitemap",
          appear: "multi",
          isVisible:
            ideaMergeEnabled &&
            (canManageChallenge ||
              util.hasPermission(user, "challenge.mergeIdeas", challengeId)),
          disabled: items.length < 2 || !canMerge,
          tooltip:
            canMerge ? `Merge ${t("generic.ideas")} together`
            : items.length > 1 ?
              `You can only merge ${t("generic.ideas")} where none of the previous ${t("generic.ideas")} have been merged`
            : `Select at least two ${t("generic.ideas")} to merge`,
          onClick: () => {
            // @ts-ignore
            confirmMerge(items, projectLaneQuery.data?.lanes, challenge)
              .then(() => {
                setSelectedIdeas([]);
                api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
                api.invalidateQueries(api.ideas.get, []);
              })
              .catch(() => {});
          },
        };
      },

      // Assign assessors
      (items) => {
        const canAssign =
          challenge.ownerIdeaTemplate?.assessmentEnabled &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.editAssessments",
              challengeId,
            )) &&
          assessmentRounds.length > 0;

        if (!canAssign) {
          return {
            name: "Assign assessors",
            icon: "user plus",
            isVisible: false,
          };
        }

        if (assessmentRounds.length > 1) {
          return {
            name: "Assign assessors",
            icon: "user plus",
            appear: "both",
            actions: assessmentRounds.map((round) => ({
              name: `Assign to ${round.name}`,
              onClick: () => {
                setAssessmentAssignRoundId(round.id);
                setAssessmentAssignIdeas(items.map((i) => i._id));
              },
            })),
          };
        }

        return {
          name: "Assign assessors",
          icon: "user plus",
          appear: "both",
          onClick: () => {
            setAssessmentAssignRoundId(assessmentRounds[0].id);
            setAssessmentAssignIdeas(items.map((i) => i._id));
          },
        };
      },

      // Move to another challenge / create from idea (non-board only)
      (items) => ({
        name: `Move to another ${t("generic.challenge")}`,
        icon: "arrow right",
        appear: "both",
        isVisible:
          ideaViewType !== "board" &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.moveIdeasToChallenge",
              challengeId,
            )),
        onClick: () => setChallengeChooserIdea(items.map((i) => i._id)),
      }),
      (items) => ({
        name: `Create ${t("generic.challenge")} from ${t("generic.idea")}`,
        icon: "target",
        appear: "single",
        isVisible:
          ideaViewType !== "board" &&
          !!items[0]?.challengeCreation &&
          (canManageChallenge ||
            util.hasPermission(
              user,
              "challenge.moveIdeasToChallenge",
              challengeId,
            )),
        onClick: () => createChallengeFromIdea(items[0]._id),
      }),

      // PDF export
      (items) => {
        const hasCreditsafe = ideaHasCreditsafeReport(items);
        if (hasCreditsafe) {
          return {
            name: "Export as PDF",
            icon: "pdf file outline",
            appear: "both",
            actions: [
              {
                name: "Regular PDF",
                onClick: () => createIdeaPdfExport(items, false),
              },
              {
                name: "With creditsafe Report",
                onClick: () => createIdeaPdfExport(items, true),
              },
            ],
          };
        }
        return {
          name: "Export as PDF",
          icon: "pdf file outline",
          appear: "both",
          onClick: () => createIdeaPdfExport(items, false),
        };
      },

      // Label
      {
        name: "Apply a label",
        icon: "bookmark",
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.editIdeaLabels", challengeId),
        onClick: (items) => setLabelIdeas(items),
      },

      // Bulk selection convenience
      {
        name: "Deselect all",
        icon: "ban",
        appear: "multi",
        onClick: () => setSelectedIdeas([]),
      },

      // Delete
      (items) => ({
        name:
          items.length === 1 && !!items[0]?.children ?
            `Delete merged ${t("generic.idea")}`
          : `Delete ${t("generic.idea")}${items.length === 1 ? "" : "s"}`,
        icon: "trash",
        appear: "both",
        isVisible:
          canManageChallenge ||
          util.hasPermission(user, "challenge.deleteIdeas", challengeId),
        tooltip:
          items.length === 1 && !!items[0]?.children ?
            `Delete this merged ${t("generic.idea")}. None of the contained ${t("generic.ideas")} will be deleted, and they will be returned to the ${t("generic.idea")} list.`
          : null,
        onClick: () => setDeletingIdeas(items),
      }),
    ];
  }, [
    assessmentRounds,
    canManageChallenge,
    challenge,
    challengeId,
    createChallengeFromIdea,
    createIdeaPdfExport,
    bulkUpdateStatus,
    ideaHasCreditsafeReport,
    ideaMergeEnabled,
    ideaViewType,
    projectLaneQuery.data?.lanes,
    setSelectedIdeas,
    t,
    user,
  ]);

  const RenderIdeaActions = useCallback(
    (ideaId: string) => {
      const idea = ideaMap[ideaId];
      if (!idea) return null;
      if (!user) return null;

      return (
        <ActionDropdown
          actions={ideaActions}
          items={[idea]}
          type="single"
          user={user}
          trigger={<Button icon="ellipsis vertical" size="mini" />}
        />
      );
    },
    [ideaActions, ideaMap, user],
  );

  const usedVotes = parseInt(challenge?.voteLimit, 10) === data.ideasVotedFor;
  const votesRemaining =
    parseInt(challenge?.voteLimit, 10) - data.ideasVotedFor;

  const activeIdeaViewType = useMemo(
    () => IdeaViewTypes.find((viewType) => viewType.key === ideaViewType),
    [ideaViewType],
  );

  const searchComponent = useMemo(
    () => (
      <Input
        size="small"
        placeholder={
          canManageChallenge || canViewProjectBoard ?
            `${t("challenge.ideas.filter.admin.search")}...`
          : `${t("challenge.ideas.filter.search")}...`
        }
        icon="search"
        value={ideaFilter}
        onChange={(e) => updateSearchTerm(e.target.value)}
        style={{
          height: 34,
          flex: 1,
          maxWidth: theme.sizes.isMobile ? undefined : 350,
        }}
        fluid
      />
    ),
    [
      canManageChallenge,
      canViewProjectBoard,
      ideaFilter,
      theme.sizes.isMobile,
      updateSearchTerm,
      t,
    ],
  );

  const numberOfFiltersNotDefault = useMemo(
    () =>
      ChallengeFilterKeys.filter((key) => {
        // If we're on the board view, we don't want to count the ideaOrder filter as a filter since we hide the filter option and it can't be changed
        if (ideaViewType === "board" && key === "ideaOrder") {
          return false;
        }
        return !util.variablesAreEqual(
          challengesReducer[key],
          challengeReducerDefaultState[key],
        );
      }).length,
    [challengesReducer, ideaViewType],
  );

  return (
    <StyledIdeasContainer>
      <ConfirmAnnouncementModal
        onClose={() => setConfirmAnnouncementModalOpen(false)}
        modalOpen={confirmAnnouncementModalOpen}
        ideaCount={
          laneUpdateContext.ideaIds ? laneUpdateContext.ideaIds.length : 0
        }
        announcements={laneAnnouncements}
        handleConfirm={(announcementIds) => {
          setConfirmAnnouncementModalOpen(false);

          updateProjectLane(
            laneUpdateContext.ideaIds,
            laneUpdateContext.laneId,
            laneUpdateContext.order,
            announcementIds,
          );

          setLaneUpdateContext({});
        }}
      />
      <ProductTour
        tour="projectBoardOnIdeasPage"
        isReady={
          !!challenge &&
          ideaViewType === "board" &&
          window.location.pathname.indexOf("/ideas") > -1
        }
      />

      <ChallengeIdeaFilters
        // @ts-ignore
        challenge={challenge}
        popularTags={ideaFilterQuery.data?.popularTags}
        allLabels={ideaFilterQuery.data?.allLabels}
        availableGroups={ideaFilterQuery.data?.availableGroups}
        availableOrganisations={ideaFilterQuery.data?.availableOrganisations}
        availableSortParameters={ideaFilterQuery.data?.availableSortParameters}
        availableFilterParameters={
          ideaFilterQuery.data?.availableFilterParameters
        }
        allProjectAssignees={ideaFilterQuery.data?.allProjectAssignees}
        availableImpacts={ideaFilterQuery.data?.availableImpacts}
      />

      <IdeaDeletionModal
        challengeId={challengeId}
        ideasToDelete={deletingIdeas}
        setIdeasToDelete={setDeletingIdeas}
        setSelectedIdeas={setSelectedIdeas}
      />
      <IdeaDeadlineModal
        challengeId={challengeId}
        deadlineIdeas={deadlineIdeas}
        setDeadlineIdeas={setDeadlineIdeas}
      />
      <IdeaManagementDeadlineModal
        managementDeadlineIdeas={managementDeadlineIdeas}
        setManagementDeadlineIdeas={setManagementDeadlineIdeas}
        updateIdeaProjectDeadlineMulti={updateIdeaProjectDeadlineMulti}
      />
      <IdeaMoveToLaneModal
        moveLaneIdeas={moveLaneIdeas}
        setMoveLaneIdeas={setMoveLaneIdeas}
        allChallengeProjectLanes={projectLaneQuery.data?.lanes || []}
        checkUpdateProjectLane={checkUpdateProjectLane}
        challenge={challenge}
      />
      <GroupUserManagementModal
        users={[
          ...new Set(
            groupAddIdeas.flatMap((idea) => {
              const userIds = [];
              if (idea.user) {
                userIds.push(idea.user);
              }
              if (idea.collaborators) {
                userIds.push(...idea.collaborators);
              }
              return userIds;
            }),
          ),
        ]}
        onClose={() => setGroupAddIdeas([])}
      />
      <HandledIdeaPreview
        totalIdeas={
          ideaViewType === "board" ? paginatedIdeas.length : data.total
        }
        currentIdeaPosition={
          paginatedIdeas.findIndex((i) => i._id === previewId) + 1
        }
        ideaId={previewId}
        assessmentEnabled={challenge.ownerIdeaTemplate?.assessmentEnabled}
        onCloseModal={() => {
          setPreviewId("");
        }}
        page={page}
        limit={limit}
        moveIdea={currentPreviewIdInList && movePreviewIdea}
        canGoNextIdea={canGoNextIdea}
        canGoPrevIdea={canGoPrevIdea}
        removeIdeaFromMerge={removeIdeaFromMerge}
        removingIdeaFromMerge={removingIdeaFromMerge}
        challenge={challenge}
        updateProjectLane={checkUpdateProjectLane}
      />
      <LabelChooser
        isOpen={labelIdeas.length > 0}
        onClose={() => setLabelIdeas([])}
        challengeId={challengeId}
        existing={[...new Set(labelIdeas.flatMap((i) => i.labels || []))]}
        onComplete={(label) =>
          api.challenges.addIdeasLabels(
            challengeId,
            { ideas: labelIdeas.map((i) => i._id!), labels: [label] },
            ({ ideas: updatedIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((i) => {
                    const updatedIdea = updatedIdeas?.find(
                      (u) => u._id === i._id,
                    );
                    if (!updatedIdea) return i;
                    return { ...i, ...updatedIdea };
                  }),
                }),
              );
              setLabelIdeas((prevIdeas) =>
                prevIdeas.map((idea) => ({
                  ...idea,
                  labels: [
                    ...(idea.labels || []).filter((l) => l !== label),
                    label,
                  ],
                })),
              );
            },
            () => {},
          )
        }
        onRemoveExisting={(label) => {
          api.challenges.removeIdeasLabels(
            challengeId,
            { ideas: labelIdeas.map((i) => i._id!), labels: [label] },
            ({ ideas: updatedIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((i) => {
                    const updatedIdea = updatedIdeas?.find(
                      (u) => u._id === i._id,
                    );
                    if (!updatedIdea) return i;
                    return { ...i, ...updatedIdea };
                  }),
                }),
              );
              setLabelIdeas((prevIdeas) =>
                prevIdeas.map((idea) => ({
                  ...idea,
                  labels: (idea.labels || []).filter((l) => l !== label),
                })),
              );
            },
            () => {},
          );
        }}
      />
      {challenge && (
        <GatedChallengeModal
          openModal={nextPhaseChallenge.length > 0}
          onCloseModal={() => setNextPhaseChallenge([])}
          challengeName={challenge.name}
          challengeId={challenge._id}
          newGroupUsers={selectedIdeas.flatMap((idea) =>
            idea.collaborators ?
              [idea.user].concat(idea.collaborators)
            : [idea.user],
          )}
        />
      )}
      <UserChooser
        isOpen={projectAssignIdeas.length > 0}
        onClose={() => setProjectAssignIdeas([])}
        onComplete={(users) => bulkProjectAssign(users.map((u) => u._id!))}
        forType="projectAssignee"
        forId={challengeId}
        clearOnComplete
        enabledFeatures={{ search: true, invite: false }}
        subtitle={`Choose multiple users to project assign to ${t("generic.idea")}.`}
        confirm="Assign these users"
        searchFunction={(term, cb, fail) =>
          api.boards.searchProjectAssignees(
            challengeId,
            term,
            ({ users }) => cb(users),
            fail,
          )
        }
      />
      <UserChooser
        isOpen={assessmentAssignIdeas.length > 0}
        onClose={() => {
          setAssessmentAssignRoundId("");
          setAssessmentAssignIdeas([]);
        }}
        onComplete={(users) => {
          // @ts-ignore
          const emailUsers = users.filter((u) => u.isEmailInvitee);
          // @ts-ignore
          const existingUsers = users.filter((u) => !u.isEmailInvitee);
          if (emailUsers.length) {
            const ideaDetails = {};
            for (const ideaId of assessmentAssignIdeas) {
              ideaDetails[ideaId] = {
                assessmentRounds: [assessmentAssignRoundId],
              };
            }

            api.invitations.createBulk(
              {
                invitees: emailUsers.map((u) => u._id),
                invitationType: "email",
                forType: "ideaAssessor",
                forId: challengeId,
                forIdeas: assessmentAssignIdeas,
                ideaDetails,
              },
              () => {
                api.invalidateQueries(api.invitations.getForType, [
                  "ideaAssessor",
                  challengeId,
                ]);
                toast.success(
                  `Invitation${emailUsers.length > 1 ? "s" : ""} sent`,
                );
              },
              () => {},
            );
            api.setQueryData(
              api.challenges.getIdeas,
              [challengeId],
              (prev) => ({
                ...prev,
                ideas: prev.ideas.map((idea) => {
                  if (assessmentAssignIdeas.indexOf(idea._id) === -1)
                    return idea;
                  return {
                    ...idea,
                    invitedExternalAssessors: [
                      ...(idea.invitedExternalAssessors || []),
                      ...emailUsers.map((u) => u._id),
                    ],
                  };
                }),
              }),
            );
          }

          const filteredExistingUsers = existingUsers.filter(
            (u) => !u.isEmailInvitee,
          );
          if (!filteredExistingUsers.length) return;

          api.challenges.assignAssessors(
            challengeId,
            assessmentAssignIdeas,
            filteredExistingUsers.map((u) => u._id),
            assessmentAssignRoundId,
            ({ ideas: assessorIdeas }) => {
              api.setQueryData(
                api.challenges.getIdeas,
                [challengeId],
                (prev) => ({
                  ...prev,
                  ideas: prev.ideas.map((idea) => {
                    const updatedIdea = assessorIdeas.find(
                      (i) => i._id === idea._id,
                    );
                    if (!updatedIdea) return idea;
                    return {
                      ...idea,
                      assessments: updatedIdea.assessments,
                      assessmentDetails: updatedIdea.assessmentDetails,
                    };
                  }),
                }),
              );
              toast.success("Assessors assigned");
            },
            (err) => toast.error(err.message),
          );
        }}
        forType="assessor"
        forId={challengeId}
        afterContent={() =>
          !challenge.visibility?.organisations?.includes(user?.organisation) ?
            <AssessorAudienceWarning />
          : null
        }
        clearOnComplete
        enabledFeatures={{ search: true, invite: true }}
        subtitle={`Choose users to assign as assessors for ${assessmentAssignIdeas.length > 1 ? `the selected ${t("generic.ideas")}` : `the selected ${t("generic.idea")}`}. Assessors will be able to find the ${t("generic.ideas")} on their 'Assessments' tab within this ${t("generic.challenge")}.`}
        confirm="Assign these assessors"
        beforeContent={() => {
          const assessmentRound = assessmentRounds.find(
            (round) => round.id === assessmentAssignRoundId,
          );
          if (!assessmentRound) return null;
          const assessmentClosed =
            (assessmentRound.id === "legacy" ?
              challenge.ownerIdeaTemplate?.assessmentClosed
            : assessmentRound.assessmentClosed) || false;
          if (!assessmentClosed) return null;

          return (
            <Message warning>
              <Message.Header>Assessment closed</Message.Header>
              <Message.Content>
                Assessment is now closed for this {t("generic.challenge")}. If
                you assign assessors, they will not be able to complete their
                assessment. You can change this{" "}
                <Link to={`/challenges/${challenge._id}/settings/format`}>
                  here
                </Link>
                .
              </Message.Content>
            </Message>
          );
        }}
        searchFunction={(term, cb, fail) =>
          api.search.challengeAssessors(
            challengeId,
            term,
            ({ users }) => cb(users),
            fail,
          )
        }
        trigger={undefined}
      />
      {challenge._id && challengeChooserIdea?.length > 0 ?
        <ChallengeChooser
          isOpen={challengeChooserIdea.length > 0}
          onClose={() => setChallengeChooserIdea([])}
          single
          subtitle={`Find the challenge you want to move the ${util.pluralise(challengeChooserIdea?.length, t("generic.idea"), t("generic.ideas"), false)} to.`}
          ignores={[challenge._id]}
          onComplete={(c) =>
            bulkMoveIdeas(challengeChooserIdea, c[0]._id, () =>
              setChallengeChooserIdea([]),
            )
          }
          beforeContent={`We'll move the selected ${t("generic.idea")}(s) to another ${t("generic.challenge")} that you can manage.`}
          afterContent={
            <Message warning>
              <p>
                <Icon name="warning" />
                <b>Caution: </b>
                {`Moving ${t("generic.ideas")} to another ${t("generic.challenge")} will have the following side effects.`}
              </p>
              <ul>
                <li>
                  The {t("generic.idea")} will be removed from the original{" "}
                  {t("generic.challenge")}
                </li>
                <li>
                  The {t("generic.idea")} will keep the {t("generic.idea")}{" "}
                  format of the original {t("generic.challenge")}
                </li>
                <li>
                  {t("common:capitalise", { key: "generic.idea" })}{" "}
                  collaborators will be removed
                </li>
                <li>Assessments will be removed</li>
                <li>Project board assignees will be removed</li>
                <li>Project board notes will be removed</li>
              </ul>
            </Message>
          }
        />
      : null}
      {ideaViewType !== "board" ?
        <ChallengeMyIdeas
          challengeId={challenge._id}
          style={{ marginBottom: 20 }}
          showMyIdeas={showMyIdeas}
          setShowMyIdeas={setShowMyIdeas}
        />
      : null}
      {user ?
        <div style={{ marginBottom: theme.sizes.isMobile ? 0 : 10 }}>
          <FilterContainer>
            <StyledFilters style={{ flex: 1 }}>
              {ideaViewType !== "board" ?
                challenge.voteLimit && challenge.votingVisibility === "users" ?
                  <Label>
                    {!usedVotes && <Icon name={"thumbs up"} />}{" "}
                    {util.pluralise(votesRemaining, "like", "likes")} remaining
                  </Label>
                : null
              : null}
              <Button
                primary
                style={{ height: 32, marginRight: 0 }}
                icon="filter"
                size="tiny"
                content={`${
                  theme.sizes.isLargeComputer ?
                    filtersVisible ? `Hide filters`
                    : `Show filters`
                  : "Filters"
                }${numberOfFiltersNotDefault ? ` (${numberOfFiltersNotDefault})` : ""}`}
                onClick={() =>
                  updateFilters({ filtersVisible: !filtersVisible })
                }
              />
              {theme.sizes.isMobile ? null : searchComponent}
              {canManageChallenge || canActionIdeas ?
                ideaViewType === "board" ?
                  <Button
                    content={
                      selectingMultipleCards ?
                        "Exit selection mode"
                      : "Select multiple cards"
                    }
                    icon="grab"
                    size="tiny"
                    onClick={() => {
                      if (selectingMultipleCards) {
                        setSelectedIdeas([]);
                      }
                      setSelectingMultipleCards((prev) => !prev);
                    }}
                    style={{ width: 180, marginRight: 0 }}
                  />
                : <Button
                    size="tiny"
                    icon="check"
                    basic
                    content={t("generic.selectAll")}
                    onClick={() =>
                      setSelectedIdeas((prevSelectedIdeas) =>
                        prevSelectedIdeas.concat(
                          data.ideas.filter(
                            (idea) =>
                              !prevSelectedIdeas
                                .map((i) => i._id)
                                .includes(idea._id),
                          ),
                        ),
                      )
                    }
                  />

              : null}
              {basicIdeaQuery.isLoading ?
                <Loader active inline="centered" />
              : null}
              {selectedIdeas && selectedIdeas.length > 0 && user && (
                <ActionDropdown
                  actions={ideaActions}
                  items={selectedIdeas}
                  type="multi"
                  user={user}
                  trigger={
                    <Button
                      primary
                      icon="dropdown"
                      size="tiny"
                      content={util.pluralise(
                        selectedIdeas.length,
                        t("generic.idea"),
                        t("generic.ideas"),
                      )}
                    />
                  }
                />
              )}
              {ideaViewType === "board" ?
                (
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.viewProjectBoard",
                    challenge._id,
                  )
                ) ?
                  <BoardEvents
                    forId={challengeId}
                    setCurrentIdeaId={setPreviewId}
                    buttonStyle={{ height: 32.5 }}
                    buttonProps={{ size: "tiny" }}
                  />
                : null
              : null}
            </StyledFilters>

            <StyledFilters>
              <Popup
                on="hover"
                content={`Change ${t("generic.idea")} view`}
                trigger={
                  <Dropdown
                    icon={null}
                    direction="left"
                    trigger={
                      <Button
                        icon={activeIdeaViewType?.icon}
                        content={activeIdeaViewType?.text}
                        size="tiny"
                        style={{ flexWrap: "nowrap", display: "flex" }}
                        className="project-board-ideas-page-1"
                      />
                    }
                    options={[
                      (
                        !theme.sizes.isLargeComputer &&
                        (util.canManageChallenge(user, challenge) ||
                          util.hasPermission(
                            user,
                            "challenge.editSettings",
                            challengeId,
                          ))
                      ) ?
                        {
                          key: "settings",
                          value: "settings",
                          as: Link,
                          to: `/challenges/${challenge._id}/settings/ideas`,
                          icon: "cogs",
                          text: `View ${t("generic.idea")} settings`,
                        }
                      : null,
                      ...IdeaViewTypes.filter((viewType) => {
                        if (viewType.key === "board") {
                          return (
                            challenge.projectManagementVisibility === "users" ||
                            canManageChallenge ||
                            util.hasPermission(
                              user,
                              "challenge.viewProjectBoard",
                              challengeId,
                            )
                          );
                        }
                        return true;
                      }).map(({ key, text, value, icon }) => ({
                        key,
                        value,
                        icon,
                        text: `${text} view`,
                      })),
                    ].filter((item) => Boolean(item))}
                    onChange={(_e, { value }) =>
                      setIdeaViewType(value as string)
                    }
                    selectOnBlur={false}
                  />
                }
              />
            </StyledFilters>
          </FilterContainer>
          {(
            ideaViewType === "board" &&
            filter === "submitted" &&
            theme.sizes.isComputer
          ) ?
            <span style={{ color: "#666", fontSize: 12 }}>
              Draft {t("generic.ideas")} are hidden by default in board view.
              You can change this in the {t("generic.idea")} filters.
            </span>
          : null}
          {theme.sizes.isMobile ?
            <div style={{ marginBottom: 5, width: "100%" }}>
              {searchComponent}
            </div>
          : null}
        </div>
      : null}
      {!user && (
        <Message>
          <h3>{t("challenge.ideas.loginRequired")}</h3>
          <p>{t("challenge.ideas.loginRequiredDescription")}</p>
          <Divider section />
          <Button
            as={Link}
            to={`/login?then=/challenges/${challenge._id}/ideas`}
            primary
          >
            {t("entry.login.loginOrSignUp")}
          </Button>
        </Message>
      )}
      {(
        user &&
        (challenge.ideaVisibility === "users" ||
          canManageChallenge ||
          util.hasPermission(user, "challenge.viewIdeas", challengeId) ||
          (util.hasPermission(
            user,
            "challenge.viewProjectBoard",
            challengeId,
          ) &&
            ideaViewType === "board"))
      ) ?
        <StyledIdeaItemsContainer>
          {ideaViewType === "list" ?
            <div>
              <IdeaTableView
                ideas={data.ideas}
                challenge={challenge}
                loading={ideasLoading}
                onSelect={setSelectedIdeas}
                selectedIdeas={selectedIdeas.map((i) => i._id)}
                ideasVotedFor={data.ideasVotedFor || 0}
                ideaActions={ideaActions}
                setPreviewOpen={setPreviewId}
              />
            </div>
          : null}
          {ideaViewType === "card" ?
            <Card.Group
              itemsPerRow={4}
              doubling
              stackable
              style={{ marginBottom: 20, zIndex: 5 }}
            >
              {data.ideas.map((idea) => (
                <IdeaCardView
                  key={idea._id}
                  idea={idea}
                  ideasVotedFor={data.ideasVotedFor || 0}
                  challenge={challenge}
                  onSelect={onSelectIdea}
                  selectedIdeas={selectedIdeas.map((i) => i._id)}
                  ideaActions={RenderIdeaActions}
                  setPreviewOpen={setPreviewId}
                  loading={ideasLoading}
                />
              ))}
            </Card.Group>
          : null}
          {ideaViewType === "board" ?
            <ProjectBoard
              forId={challengeId!}
              ideas={data.ideas!}
              ideasLoading={ideasLoading}
              defaultLane={challenge.projectBoard?.defaultLane}
              projectManagementVisibility={
                challenge.projectManagementVisibility
              }
              setPreviewId={setPreviewId}
              isSelecting={selectingMultipleCards}
              selectedIdeas={selectedIdeas}
              setSelectedIdeas={setSelectedIdeas}
              laneFilter={projectBoardLanesFilter}
              checkUpdateProjectLane={checkUpdateProjectLane}
            />
          : null}
          {(
            !basicIdeaQuery.isFetching &&
            data.ideas.length === 0 &&
            ideaViewType !== "board"
          ) ?
            <EmptyBox>
              <h2>{t("challenge.ideas.noneTitle")}</h2>
              {challenge.stage === "published" && canMakeIdea && (
                <Button
                  primary
                  icon="plus"
                  content={
                    challenge.buttonText ?
                      challenge.buttonText
                    : t("challenge.ideas.createYourIdea")
                  }
                  onClick={() => newIdea()}
                />
              )}
            </EmptyBox>
          : null}
          {(
            !basicIdeaQuery.isFetching &&
            data.ideas.length > 0 &&
            ideaViewType !== "board"
          ) ?
            <Pagination
              limit={limit}
              limitValues={
                ideaViewType === "list" ? [15, 30, 50, 100] : [16, 32, 52, 100]
              }
              page={page}
              total={data.total}
              updateFilters={updateFilters}
            />
          : null}
        </StyledIdeaItemsContainer>
      : null}
    </StyledIdeasContainer>
  );
};

export default Ideas;
